import type { TFunction } from 'i18next'

export type ImageGenerationErrorCategory =
  | 'NETWORK_UNREACHABLE'
  | 'BACKEND_ERROR'
  | 'PROVIDER_ERROR'
  | 'REFERENCE_IMAGE_ERROR'
  | 'INVALID_PARAMETER'
  | 'RATE_LIMIT'
  | 'CREDENTIAL_ERROR'
  | 'GENERATION_TIMEOUT'
  | 'ROUTING_ERROR'
  // VistoeraTASK-00148 (§四/§十一) — infra-side Worker Lease/heartbeat
  // loss (`WORKER_INTERRUPTED`, canonical `LEASE_EXPIRED_MAX_ATTEMPTS`
  // relay). Deliberately its OWN category, never folded into
  // 'PROVIDER_ERROR'/'UNKNOWN_ERROR' — the Provider never rejected
  // anything and no request parameter was ever invalid; the execution
  // node (local Worker OR Browser AI/PC3) simply stopped responding.
  // Tenant copy is "任务中断，可重新生成", never "生成失败"/"请求参数无效".
  | 'WORKER_INTERRUPTED'
  | 'UNKNOWN_ERROR'

export interface ImageGenerationErrorPresentation {
  category: ImageGenerationErrorCategory
  message: string
  code?: string | null
  taskId?: string | null
}

const REFERENCE_ERROR_CODES = new Set([
  'FAST_REFERENCE_UNSUPPORTED',
  'REFERENCE_REQUIRES_CLOUD_PROVIDER',
  'REFERENCE_LIMIT_EXCEEDED',
  'TOO_MANY_REFERENCE_IMAGES',
  'IMAGE_FETCH_FAILED',
])

const PROVIDER_ERROR_CODES = new Set([
  'PROVIDER_TIMEOUT',
  'PROVIDER_NETWORK_ERROR',
  'PROVIDER_UNAVAILABLE',
  'PROVIDER_EXECUTION_NOT_SUPPORTED',
  'CONTENT_MODERATED',
  'REQUEST_MODERATED',
  'GENERATION_FAILED',
])

const ROUTING_ERROR_CODES = new Set([
  'IMAGE_GENERATION_ROUTE_UNAVAILABLE',
  'PROVIDER_NOT_ENABLED_FOR_FUNCTION_NODE',
  'MODEL_NOT_BOUND_TO_FUNCTION_NODE',
  'PROVIDER_CHANNEL_MISMATCH',
  // Legacy — canonical image-generation route no longer returns this (TASK-00303).
  'CLOUD_PROVIDER_NOT_ENTITLED',
  'CLOUD_FALLBACK_NOT_ALLOWED',
])

const INVALID_PARAMETER_CODES = new Set(['INVALID_REQUEST', 'PROMPT_EXCEEDS_PROVIDER_LIMIT'])

const TIMEOUT_CODES = new Set(['GENERATION_TIMEOUT', 'GPU_BUSY_TIMEOUT', 'PROVIDER_TIMEOUT', 'ENGINE_START_TIMEOUT'])

// CLOUD_PROVIDER_NOT_ENTITLED kept for legacy API payloads only (TASK-00303).
const CREDENTIAL_CODES = new Set(['CLOUD_PROVIDER_NOT_ENTITLED', 'TRANSLATION_CONFIG_MISSING'])

// VistoeraTASK-00148 (§四/§十一) — Worker Lease genuinely exhausted
// (infra interruption), never a Provider/generation-content rejection.
const WORKER_INTERRUPTED_CODES = new Set(['WORKER_INTERRUPTED', 'LEASE_EXPIRED_MAX_ATTEMPTS'])

function categoryForCode(code: string | null | undefined): ImageGenerationErrorCategory {
  const normalized = (code || '').trim().toUpperCase()
  if (!normalized) return 'UNKNOWN_ERROR'
  if (normalized === 'TRANSPORT_ERROR') return 'NETWORK_UNREACHABLE'
  if (WORKER_INTERRUPTED_CODES.has(normalized)) return 'WORKER_INTERRUPTED'
  if (REFERENCE_ERROR_CODES.has(normalized)) return 'REFERENCE_IMAGE_ERROR'
  if (ROUTING_ERROR_CODES.has(normalized)) return 'ROUTING_ERROR'
  if (INVALID_PARAMETER_CODES.has(normalized)) return 'INVALID_PARAMETER'
  if (TIMEOUT_CODES.has(normalized)) return 'GENERATION_TIMEOUT'
  if (CREDENTIAL_CODES.has(normalized)) return 'CREDENTIAL_ERROR'
  if (normalized === 'RATE_LIMITED' || normalized === 'RATE_LIMIT') return 'RATE_LIMIT'
  if (PROVIDER_ERROR_CODES.has(normalized)) return 'PROVIDER_ERROR'
  if (normalized.endsWith('_UNAVAILABLE') || normalized === 'WORKER_INTERNAL_ERROR') return 'BACKEND_ERROR'
  return 'UNKNOWN_ERROR'
}

export function localizeImageGenerationTaskError(
  args: {
    errorCode?: string | null
    errorMessage?: string | null
    taskId?: string | null
  },
  t: TFunction<'canvas'>,
): ImageGenerationErrorPresentation {
  const code = args.errorCode?.trim() || null
  const category = categoryForCode(code)
  const backendMessage = (args.errorMessage || '').trim()

  let message: string
  switch (category) {
    case 'NETWORK_UNREACHABLE':
      message = t('generation.errors.networkUnreachable')
      break
    case 'REFERENCE_IMAGE_ERROR':
      message =
        code === 'REFERENCE_REQUIRES_CLOUD_PROVIDER'
          ? t('generation.errors.referenceRequiresCloudProvider')
          : code === 'IMAGE_FETCH_FAILED'
            ? t('generation.errors.referenceImageFailed')
            : backendMessage || t('generation.errors.referenceImageFailed')
      break
    case 'ROUTING_ERROR':
      message = backendMessage || t('generation.errors.providerRoutingFailed')
      break
    case 'INVALID_PARAMETER':
      message = backendMessage || t('generation.errors.invalidParameters')
      break
    case 'PROVIDER_ERROR':
      message = backendMessage || t('generation.errors.providerFailed')
      break
    case 'GENERATION_TIMEOUT':
      message = backendMessage || t('generation.errors.generationTimeout')
      break
    case 'CREDENTIAL_ERROR':
      message = backendMessage || t('generation.errors.credentialError')
      break
    case 'WORKER_INTERRUPTED':
      message = backendMessage || t('generation.errors.workerInterrupted')
      break
    case 'RATE_LIMIT':
      message = t('generation.errors.rateLimited')
      break
    case 'BACKEND_ERROR':
      message = backendMessage || t('generation.errors.backendError')
      break
    default:
      message = backendMessage || t('generation.errors.unknown')
      break
  }

  return { category, message, code, taskId: args.taskId ?? null }
}

export function localizeImageGenerationSubmitApiError(
  args: { code?: string | null; message?: string | null; status?: number | null },
  t: TFunction<'canvas'>,
): ImageGenerationErrorPresentation {
  const code = args.code?.trim() || null
  if (code === 'member_credit_limit_exceeded') {
    return {
      category: 'BACKEND_ERROR',
      message: t('generation.errors.memberCreditLimitExceeded'),
      code,
    }
  }
  if (code === 'insufficient_credits' || args.status === 402) {
    return {
      category: 'BACKEND_ERROR',
      message: t('generation.errors.insufficientCredits'),
      code: code ?? 'insufficient_credits',
    }
  }
  if (code === 'TRANSPORT_ERROR') {
    return {
      category: 'NETWORK_UNREACHABLE',
      message: t('generation.errors.networkUnreachable'),
      code,
    }
  }
  if (args.status != null) {
    return localizeImageGenerationTaskError({ errorCode: code, errorMessage: args.message }, t)
  }
  if (code) {
    return localizeImageGenerationTaskError({ errorCode: code, errorMessage: args.message }, t)
  }
  const message = (args.message || '').trim() || t('generation.errors.unknown')
  return { category: 'UNKNOWN_ERROR', message, code: null }
}
